// "Fill in" on the company form: proposals appear under each field, each with Accept, plus Accept all.
import { Check, Loader2, Sparkles } from "lucide-react";
import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { createContext, useContext, useState, type ReactNode } from "react";
import { useFormContext } from "react-hook-form";
import { Button } from "@/components/ui/button";

import { getTranslatedCompanySizeLabel } from "../companies/getTranslatedCompanySizeLabel";
import { sizes } from "../companies/sizes";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { CrmDataProvider } from "../providers/types";
import { runAiJson } from "./ai";
import { useEngineStatus } from "./leads/leads";
import {
  AUTOFILL_FIELDS,
  buildExtractPrompt,
  ExtractCompanySchema,
  buildWebsitePrompt,
  logoProposal,
  lookupCompany,
  MAX_WEBSITE_GUESSES,
  namesCompany,
  proposalsFrom,
  uniqueSites,
  WebsiteGuessSchema,
  type AutofillField,
  type CompanyLookup,
  type Proposals,
} from "./companyLookup";

type AutofillState = {
  proposals: Proposals;
  setProposals: (next: Proposals) => void;
};
const AutofillContext = createContext<AutofillState>({
  proposals: {},
  setProposals: () => {},
});

export const CompanyAutofillProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [proposals, setProposals] = useState<Proposals>({});
  return (
    <AutofillContext.Provider value={{ proposals, setProposals }}>
      {children}
    </AutofillContext.Provider>
  );
};

const useAccept = () => {
  const { proposals, setProposals } = useContext(AutofillContext);
  const { setValue } = useFormContext();
  return (fields: AutofillField[]) => {
    const rest = { ...proposals };
    for (const field of fields) {
      if (rest[field] === undefined) continue;
      // the logo input holds an image object, as the database's own favicon fill writes it
      const value =
        field === "logo"
          ? { src: rest[field], title: "Company logo" }
          : rest[field];
      setValue(field, value, { shouldDirty: true });
      delete rest[field];
    }
    setProposals(rest);
  };
};

/** Reads the website (found from the name when it is empty), asks the AI, and shows its proposals under the fields */
export const CompanyFillInButton = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const { companySectors } = useConfigurationContext();
  const { getValues, setValue } = useFormContext();
  const { proposals, setProposals } = useContext(AutofillContext);
  const accept = useAccept();
  const [busy, setBusy] = useState(false);
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { data: engines = [] } = useEngineStatus();
  const apolloKey = engines.some((e) => e.engine === "apollo" && e.has_key);
  const pending = AUTOFILL_FIELDS.filter((f) => proposals[f] !== undefined);

  const [candidates, setCandidates] = useState<CompanyLookup[]>([]);

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    try {
      await work();
    } catch (error) {
      console.error("company autofill failed", error);
      notify("resources.companies.autofill.failed", {
        type: "error",
        messageArgs: { error: (error as Error).message },
      });
    } finally {
      setBusy(false);
    }
  };

  /** Details and logo from the website: Apollo's enrichment first when an admin added its key, else the site itself */
  const fillFrom = async (website: string, known?: CompanyLookup) => {
    const values = getValues();
    let next: Proposals = apolloKey
      ? await dataProvider
          .leadSearch<{ organization: unknown }>({
            action: "enrich_company",
            domain: website,
          })
          .then(({ organization }) => {
            if (!organization) return {};
            const found = proposalsFrom(
              ExtractCompanySchema.parse(organization),
              companySectors,
              values,
            );
            const logo = logoProposal(
              String(found.website ?? website),
              (organization as { logo?: unknown }).logo,
              values,
            );
            return logo ? { logo, ...found } : found;
          })
          .catch((error) => {
            console.warn(
              "Apollo enrichment failed, reading the website",
              error,
            );
            return {};
          })
      : {};
    if (!Object.keys(next).length) {
      const lookup = known ?? (await lookupCompany(website));
      const answer = await runAiJson(
        "extract_company",
        buildExtractPrompt(String(values.name ?? ""), lookup, companySectors),
        ExtractCompanySchema,
      );
      next = proposalsFrom(answer, companySectors, values);
      const logo = logoProposal(lookup.website, lookup.logo, values);
      if (logo) next = { logo, ...next };
    }
    setProposals(next);
    if (!Object.keys(next).length)
      notify("resources.companies.autofill.nothing", { type: "info" });
  };

  /** Name only: Apollo's name search (with a key) and the AI's guesses, each site opened and kept if it names the company */
  const findWebsites = async (name: string) => {
    const country = String(getValues().country ?? "").trim() || undefined;
    const [fromApollo, guess] = await Promise.all([
      apolloKey
        ? dataProvider
            .leadSearch<{ companies: { website: string }[] }>({
              action: "find_company",
              name,
            })
            .then(({ companies }) => companies.map((c) => c.website))
            .catch(() => [] as string[])
        : Promise.resolve([] as string[]),
      runAiJson(
        "find_website",
        buildWebsitePrompt(name, country),
        WebsiteGuessSchema,
      ).catch(() => ({ websites: [] })),
    ]);
    const sites = uniqueSites([
      ...fromApollo,
      ...(guess.websites ?? []).slice(0, MAX_WEBSITE_GUESSES),
    ]);
    const lookups = await Promise.all(
      sites.map((site) => lookupCompany(site).catch(() => null)),
    );
    const seen = new Set<string>();
    const found = lookups.filter((lookup): lookup is CompanyLookup => {
      if (!lookup || seen.has(lookup.website) || !namesCompany(name, lookup))
        return false;
      seen.add(lookup.website);
      return true;
    });
    setCandidates(found.slice(0, MAX_WEBSITE_GUESSES));
    if (!found.length)
      notify("resources.companies.autofill.no_match", { type: "info" });
  };

  const fillIn = () => {
    const { website, name } = getValues();
    const site = String(website ?? "").trim();
    const company = String(name ?? "").trim();
    if (!site && !company) {
      notify("resources.companies.autofill.need_name", { type: "warning" });
      return;
    }
    setCandidates([]);
    return run(() => (site ? fillFrom(site) : findWebsites(company)));
  };

  /** The User picked one of the sites found from the name: it becomes the website, then the details are proposed */
  const pick = (lookup: CompanyLookup) => {
    setCandidates([]);
    setValue("website", lookup.website, { shouldDirty: true });
    return run(() => fillFrom(lookup.website, lookup));
  };

  return (
    <div className="flex gap-2 items-center flex-wrap">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={fillIn}
        disabled={busy}
        title={translate("resources.companies.autofill.help")}
      >
        {busy ? <Loader2 className="animate-spin" /> : <Sparkles />}
        {translate(
          busy
            ? "resources.companies.autofill.reading"
            : "resources.companies.autofill.fill_in",
        )}
      </Button>
      {candidates.length > 0 && (
        <div className="basis-full flex flex-col gap-1 rounded-md bg-muted px-2 py-2 text-sm">
          <span className="text-muted-foreground">
            {translate("resources.companies.autofill.pick")}
          </span>
          {candidates.map((lookup) => (
            <Button
              key={lookup.website}
              type="button"
              variant="outline"
              size="sm"
              className="justify-start h-auto min-h-9 whitespace-normal text-left"
              onClick={() => pick(lookup)}
              disabled={busy}
            >
              <span className="font-medium">
                {lookup.website.replace(/^https:\/\/(www\.)?/, "")}
              </span>
              <span className="text-muted-foreground truncate">
                {lookup.pages[0]?.text.split("\n")[0].slice(0, 80)}
              </span>
            </Button>
          ))}
        </div>
      )}
      {pending.length > 0 && (
        <Button type="button" size="sm" onClick={() => accept(pending)}>
          <Check />
          {translate("resources.companies.autofill.accept_all", {
            smart_count: pending.length,
          })}
        </Button>
      )}
    </div>
  );
};

/** "Suggested: …  Accept" under a field, when Fill in proposed a value for it */
export const AutofillHint = ({ source }: { source: AutofillField }) => {
  const translate = useTranslate();
  const { companySectors } = useConfigurationContext();
  const { proposals } = useContext(AutofillContext);
  const accept = useAccept();
  const value = proposals[source];
  if (value === undefined) return null;
  const shown =
    source === "logo" ? (
      <img
        src={String(value)}
        alt=""
        className="inline-block size-8 rounded object-contain align-middle bg-background"
        referrerPolicy="no-referrer"
      />
    ) : source === "sector" ? (
      (companySectors.find((s) => s.value === value)?.label ?? value)
    ) : source === "size" ? (
      getTranslatedCompanySizeLabel(
        sizes.find((s) => s.id === value) ?? sizes[0],
        translate,
      )
    ) : (
      value
    );
  return (
    <div className="-mt-2 flex items-start gap-2 rounded-md bg-muted px-2 py-1 text-sm">
      <span className="flex-1 min-w-0 break-words">
        <span className="text-muted-foreground">
          {translate("resources.companies.autofill.suggested")}{" "}
        </span>
        {shown}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-6 px-2"
        onClick={() => accept([source])}
      >
        {translate("resources.companies.autofill.accept")}
      </Button>
    </div>
  );
};
